/**
 * Reporte: Movimiento de relaciones laborales — Fase 7.3 (reporte 7 de 7)
 *
 * Altas y bajas de afiliados por empresa en un rango de fechas.
 * Filtros: empresa (opcional — si no se selecciona, todas), desde/hasta
 * (requeridos al menos uno).
 *
 * Cada movimiento es una fila con:
 *   - tipo: 'alta' o 'baja'
 *   - fecha del evento
 *   - afiliado (apellido, nombre, CUIL)
 *   - empresa
 *   - sucursal / categoría
 *   - motivo (solo bajas — viene de observaciones)
 */

import { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Stack,
  Typography,
  Card,
  Button,
  TextField,
  Autocomplete,
  Alert,
  Chip,
  Divider,
} from '@mui/material';
import {
  DataGrid,
  type GridColDef,
  type GridRenderCellParams,
} from '@mui/x-data-grid';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableViewIcon from '@mui/icons-material/TableView';
import SwapVertIcon from '@mui/icons-material/SwapVert';

import { useQuery } from '@tanstack/react-query';
import { supabase } from '../../services/supabase';
import { useEmpresas } from '../../hooks/useEmpresas';
import { formatDate, formatCuit } from '../../utils/format';

import {
  createReportePdf,
  addReporteTable,
  addReporteFooter,
  fmtDate,
  fmtText,
  type ColumnaPdf,
} from '../../utils/reportes/pdf';
import { generarReporteExcel, type ColumnaExcel } from '../../utils/reportes/excel';
import { triggerBlobDownload, buildReporteFilename } from '../../utils/reportes/download';

// ── Tipos auxiliares ──────────────────────────────────────────────────────────

type TipoMovimiento = 'alta' | 'baja';

interface MovimientoRow {
  id: string;
  tipo: TipoMovimiento;
  fecha: string;
  apellido: string;
  nombre: string;
  cuil: string;
  empresa_razon_social: string;
  empresa_cuit: string;
  sucursal: string;
  categoria: string;
  motivo: string;
}

interface RelacionConJoins {
  id: string;
  fecha_alta: string;
  fecha_baja: string | null;
  observaciones: string | null;
  empresa: { razon_social: string; cuit: string } | null;
  sucursal: { denominacion: string | null } | null;
  categoria: { descripcion: string } | null;
  afiliado: { cuil: string; apellido: string; nombre: string } | null;
}

// ── Hook query inline (no se reutiliza desde otro lado) ──────────────────────

interface FiltroMovimientos {
  empresaId?: string | null;
  desde?: string;
  hasta?: string;
}

function useMovimientosRelaciones(filtro: FiltroMovimientos) {
  return useQuery({
    queryKey: ['reportes', 'movimientos-relaciones', filtro],
    queryFn: async () => {
      // Traemos todas las relaciones cuya alta O baja caigan dentro del rango.
      // Es más simple traerlas todas filtradas por empresa (si hay) y filtrar
      // en cliente que armar un OR complejo en PostgREST.
      let q = supabase
        .from('relaciones_laborales')
        .select(
          `id, fecha_alta, fecha_baja, observaciones,
           empresa:empresas(razon_social, cuit),
           sucursal:sucursales(denominacion),
           categoria:categorias(descripcion),
           afiliado:afiliados(cuil, apellido, nombre)`,
        );
      if (filtro.empresaId) q = q.eq('empresa_id', filtro.empresaId);

      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as unknown as RelacionConJoins[];
    },
    // Solo consulta si hay al menos un filtro de fecha (sin rango no tiene sentido)
    enabled: !!(filtro.desde || filtro.hasta),
  });
}

// ── Componente ────────────────────────────────────────────────────────────────

export default function MovimientoRelacionesReporte() {
  const [empresaId, setEmpresaId] = useState<string | null>(null);
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [incluirAltas, setIncluirAltas] = useState(true);
  const [incluirBajas, setIncluirBajas] = useState(true);

  const { data: empresasResult } = useEmpresas({ pageSize: 1000 });
  const empresas = empresasResult?.data ?? [];

  const {
    data: relaciones = [],
    isLoading,
    isError,
  } = useMovimientosRelaciones({ empresaId, desde, hasta });

  // ── Expandir cada relación a 0/1/2 filas de movimiento ────────────────
  const rows: MovimientoRow[] = useMemo(() => {
    const enRango = (fecha: string): boolean => {
      if (desde && fecha < desde) return false;
      if (hasta && fecha > hasta) return false;
      return true;
    };

    const result: MovimientoRow[] = [];

    for (const r of relaciones) {
      const base = {
        apellido: r.afiliado?.apellido ?? '—',
        nombre: r.afiliado?.nombre ?? '—',
        cuil: r.afiliado?.cuil ?? '',
        empresa_razon_social: r.empresa?.razon_social ?? '—',
        empresa_cuit: r.empresa?.cuit ?? '',
        sucursal: r.sucursal?.denominacion ?? '—',
        categoria: r.categoria?.descripcion ?? '—',
      };

      if (incluirAltas && enRango(r.fecha_alta)) {
        result.push({
          ...base,
          id: `${r.id}-alta`,
          tipo: 'alta',
          fecha: r.fecha_alta,
          motivo: '',
        });
      }
      if (incluirBajas && r.fecha_baja && enRango(r.fecha_baja)) {
        result.push({
          ...base,
          id: `${r.id}-baja`,
          tipo: 'baja',
          fecha: r.fecha_baja,
          motivo: r.observaciones ?? '',
        });
      }
    }

    // Ordenar por fecha del evento (más reciente primero)
    return result.sort((a, b) => b.fecha.localeCompare(a.fecha));
  }, [relaciones, desde, hasta, incluirAltas, incluirBajas]);

  const totales = useMemo(
    () => ({
      total: rows.length,
      altas: rows.filter((r) => r.tipo === 'alta').length,
      bajas: rows.filter((r) => r.tipo === 'baja').length,
    }),
    [rows],
  );

  const empresa = empresas.find((e) => e.id === empresaId);

  const subtitulo = useMemo(() => {
    const partes: string[] = [];
    if (empresa) partes.push(`Empresa: ${empresa.razon_social}`);
    else partes.push('Todas las empresas');
    if (desde) partes.push(`desde ${formatDate(desde)}`);
    if (hasta) partes.push(`hasta ${formatDate(hasta)}`);
    return partes.join(' · ');
  }, [empresa, desde, hasta]);

  // ── Export PDF ─────────────────────────────────────────────────────────
  const handleExportPdf = () => {
    const doc = createReportePdf({
      titulo: 'Movimiento de relaciones laborales',
      subtitulo,
      orientacion: 'landscape',
    });

    const columnas: ColumnaPdf[] = [
      { header: 'Tipo', align: 'center', format: (v) => String(v).toUpperCase() },
      { header: 'Fecha', align: 'center', format: fmtDate },
      { header: 'Apellido', format: fmtText },
      { header: 'Nombre', format: fmtText },
      { header: 'CUIL', align: 'center', format: (v) => formatCuit(String(v)) },
      { header: 'Empresa', format: fmtText },
      { header: 'Categoría', format: fmtText },
      { header: 'Motivo (bajas)', format: fmtText },
    ];

    const body = rows.map((r) => [
      r.tipo,
      r.fecha,
      r.apellido,
      r.nombre,
      r.cuil,
      r.empresa_razon_social,
      r.categoria,
      r.motivo,
    ]);

    addReporteTable(doc, columnas, body, {
      titulo: 'Movimiento de relaciones laborales',
      subtitulo,
    });
    addReporteFooter(doc);

    const blob = doc.output('blob');
    triggerBlobDownload(blob, buildReporteFilename('movimiento-relaciones', 'pdf'));
  };

  // ── Export Excel ───────────────────────────────────────────────────────
  const handleExportExcel = async () => {
    const columnas: ColumnaExcel[] = [
      { header: 'Tipo', key: 'tipo', width: 8, format: 'text' },
      { header: 'Fecha', key: 'fecha', width: 12, format: 'date' },
      { header: 'Apellido', key: 'apellido', width: 22, format: 'text' },
      { header: 'Nombre', key: 'nombre', width: 22, format: 'text' },
      { header: 'CUIL', key: 'cuil', width: 14, format: 'text' },
      { header: 'Empresa', key: 'empresa', width: 30, format: 'text' },
      { header: 'CUIT empresa', key: 'cuitEmp', width: 14, format: 'text' },
      { header: 'Sucursal', key: 'sucursal', width: 18, format: 'text' },
      { header: 'Categoría', key: 'categoria', width: 28, format: 'text' },
      { header: 'Motivo (bajas)', key: 'motivo', width: 30, format: 'text' },
    ];

    const rowsExcel = rows.map((r) => ({
      tipo: r.tipo.toUpperCase(),
      fecha: new Date(r.fecha),
      apellido: r.apellido,
      nombre: r.nombre,
      cuil: formatCuit(r.cuil),
      empresa: r.empresa_razon_social,
      cuitEmp: formatCuit(r.empresa_cuit),
      sucursal: r.sucursal,
      categoria: r.categoria,
      motivo: r.motivo,
    }));

    const blob = await generarReporteExcel({
      titulo: 'Movimiento de relaciones laborales',
      subtitulo,
      nombreHoja: 'Movimientos',
      columnas,
      rows: rowsExcel,
    });
    triggerBlobDownload(blob, buildReporteFilename('movimiento-relaciones', 'xlsx'));
  };

  // ── DataGrid ────────────────────────────────────────────────────────────
  const columns: GridColDef<MovimientoRow>[] = [
    {
      field: 'tipo',
      headerName: 'Tipo',
      width: 90,
      renderCell: (p: GridRenderCellParams<MovimientoRow>) => (
        <Chip
          label={p.row.tipo === 'alta' ? 'Alta' : 'Baja'}
          color={p.row.tipo === 'alta' ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'fecha',
      headerName: 'Fecha',
      width: 110,
      renderCell: (p) => formatDate(p.row.fecha),
    },
    { field: 'apellido', headerName: 'Apellido', flex: 1, minWidth: 140 },
    { field: 'nombre', headerName: 'Nombre', flex: 1, minWidth: 140 },
    {
      field: 'cuil',
      headerName: 'CUIL',
      width: 140,
      renderCell: (p) => formatCuit(p.row.cuil),
    },
    {
      field: 'empresa_razon_social',
      headerName: 'Empresa',
      flex: 1.5,
      minWidth: 180,
    },
    {
      field: 'categoria',
      headerName: 'Categoría',
      flex: 1.2,
      minWidth: 160,
    },
    {
      field: 'motivo',
      headerName: 'Motivo (bajas)',
      flex: 1.5,
      minWidth: 180,
    },
  ];

  const sinFiltroFecha = !desde && !hasta;
  const sinEstado = !incluirAltas && !incluirBajas;
  const sinDatos = !sinFiltroFecha && !isLoading && !isError && rows.length === 0;

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        component={RouterLink}
        to="/reportes"
        sx={{ mb: 2 }}
      >
        Volver a Reportes
      </Button>

      <Stack direction="row" spacing={2} alignItems="center" mb={2}>
        <SwapVertIcon color="primary" sx={{ fontSize: 32 }} />
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Movimiento de relaciones laborales
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Altas y bajas de afiliados por empresa en un rango de fechas.
          </Typography>
        </Box>
      </Stack>

      {/* Filtros */}
      <Card sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Filtros
        </Typography>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          <Autocomplete
            sx={{ minWidth: 280 }}
            size="small"
            options={empresas}
            getOptionLabel={(opt) => opt.razon_social}
            value={empresas.find((e) => e.id === empresaId) ?? null}
            onChange={(_, val) => setEmpresaId(val?.id ?? null)}
            renderInput={(params) => (
              <TextField {...params} label="Empresa (todas si vacío)" />
            )}
          />
          <TextField
            type="date"
            size="small"
            label="Desde (requerido)"
            required
            value={desde}
            onChange={(e) => setDesde(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 180 }}
          />
          <TextField
            type="date"
            size="small"
            label="Hasta (requerido)"
            required
            value={hasta}
            onChange={(e) => setHasta(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 180 }}
          />
          <Stack direction="row" spacing={0.5}>
            <Chip
              label="Altas"
              color={incluirAltas ? 'success' : 'default'}
              variant={incluirAltas ? 'filled' : 'outlined'}
              onClick={() => setIncluirAltas(!incluirAltas)}
              size="small"
              clickable
            />
            <Chip
              label="Bajas"
              color={incluirBajas ? 'warning' : 'default'}
              variant={incluirBajas ? 'filled' : 'outlined'}
              onClick={() => setIncluirBajas(!incluirBajas)}
              size="small"
              clickable
            />
          </Stack>
        </Stack>
      </Card>

      {/* Totales + Export */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ sm: 'center' }}
        mb={2}
      >
        <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap" useFlexGap>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Total movimientos
            </Typography>
            <Typography variant="h6" fontWeight={700}>
              {totales.total}
            </Typography>
          </Box>
          <Divider orientation="vertical" flexItem />
          <Box>
            <Typography variant="caption" color="text.secondary">
              Altas
            </Typography>
            <Typography variant="h6" fontWeight={700} color="success.main">
              {totales.altas}
            </Typography>
          </Box>
          <Divider orientation="vertical" flexItem />
          <Box>
            <Typography variant="caption" color="text.secondary">
              Bajas
            </Typography>
            <Typography variant="h6" fontWeight={700} color="warning.main">
              {totales.bajas}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<PictureAsPdfIcon />}
            onClick={handleExportPdf}
            disabled={rows.length === 0}
          >
            Descargar PDF
          </Button>
          <Button
            variant="outlined"
            startIcon={<TableViewIcon />}
            onClick={handleExportExcel}
            disabled={rows.length === 0}
          >
            Descargar Excel
          </Button>
        </Stack>
      </Stack>

      {sinFiltroFecha && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Ingresá al menos una fecha (desde o hasta) para ver los movimientos.
        </Alert>
      )}
      {sinEstado && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Tenés que incluir al menos un tipo: Altas o Bajas.
        </Alert>
      )}
      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Error al cargar los movimientos.
        </Alert>
      )}
      {sinDatos && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No hay movimientos en el rango de fechas seleccionado.
        </Alert>
      )}

      <Card>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={isLoading}
          autoHeight
          disableRowSelectionOnClick
          pageSizeOptions={[25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          sx={{ border: 0 }}
        />
      </Card>
    </Box>
  );
}
